import type {
    CorePlugin,
    Deeplink,
    FPSPaymentOptions,
    FPSResultOptions,
    Member,
} from './definitions';
import { HuaweiBridgeClient } from './huawei-bridge-client';

/**
 * CoreHuawei implements the same CorePlugin interface as aigens-sdk-core,
 * but routes all method calls through the Huawei JS Bridge to HarmonyOS native.
 *
 * This class is used when `isHuaWei()` returns true (Huawei WebView detected via UA).
 * It serves as a drop-in replacement for Capacitor's `Core` plugin object.
 */
export class CoreHuawei implements CorePlugin {
    private bridge: HuaweiBridgeClient;

    constructor() {
        this.bridge = HuaweiBridgeClient.getInstance();
    }

    async getMember(options: any): Promise<{ member: Member }> {
        return this.bridge.call('getMember', options || {});
    }

    async getDeeplink(options: any): Promise<{ deeplink: Deeplink }> {
        return this.bridge.call('getDeeplink', options || {});
    }

    async dismiss(options: any): Promise<any> {
        return this.bridge.call('dismiss', { closedData: options });
    }

    async finish(options: any): Promise<any> {
        return this.bridge.call('finish', { closedData: options });
    }

    async isInstalledApp(options: { key: string }): Promise<{ install: boolean }> {
        return this.bridge.call('isInstalledApp', options);
    }

    async isDebug(): Promise<{ debug: boolean }> {
        return this.bridge.call('isDebug');
    }

    async isHuawei(): Promise<{ huawei: boolean }> {
        const huawei = navigator.userAgent.toLowerCase().includes('huawei-aigens');
        return { huawei };
    }

    async openExternalUrl(options: { url: string }): Promise<any> {
        return this.bridge.call('openExternalUrl', options);
    }

    async setTextZoom(options: { value: number }): Promise<any> {
        return this.bridge.call('setTextZoom', options);
    }

    async readClipboard(): Promise<any> {
        return this.bridge.call('readClipboard');
    }

    async makeHKFPSPayment(options: FPSPaymentOptions): Promise<FPSResultOptions> {
        return this.bridge.call('makeHKFPSPayment', options);
    }

    async setNativeBackPressEnabled(options: { enable: boolean }): Promise<{ enabled: boolean }> {
        return this.bridge.call('setNativeBackPressEnabled', options);
    }

    setNativeBackPressHandler(callback: () => void): void {
        window.aigensNativeBackPress = callback;
        this.bridge.call('setNativeBackPressHandler', {}).then((res: { enabled?: boolean }) => {
            if (res && res.enabled === false) {
                console.warn('setNativeBackPressHandler: NativeBackPressEnabled = false, handler will not fire');
            }
        }).catch((e: Error) => {
            console.error('setNativeBackPressHandler: bridge call failed: ' + e.message);
        });
    }
}
